/**
 * 激活码（Alice 页面 codes 的复刻）：四张统计卡、筛选栏、码列表、批次统计、生成与详情弹窗、封禁名单。
 *
 * 背景：激活码的生成、兑换、封锁逻辑在控制面 gateway_control/activation.py，接口是 /control/admin/activation-*。
 *   兑换入口（用户在控制台输入码）属于控制台范围，后端内部接口 /internal/activation/redeem 已备好，本页只管运营侧。
 *   Alice 的外观码、限免拨款额度码在本仓没有对应业务，码的权益类型只有花瓣；金额单位是花瓣（Alice 是糖豆）。
 * 设计意图：
 *   - 列表与批次统计分别请求（统计不随筛选变化，批次表是全局视图）；改了码或批次后两处一起刷新。
 *   - 「导出批次 CSV」需要先填批次号，用浏览器直接下载接口地址（同源 Cookie 鉴权，CSV 含分享链接）。
 *   - 整批操作（禁用、启用、改备注、删除）放在批次统计表里，和 Alice 一致；删除批次与删除码都要确认，并说明已到账的花瓣不收回。
 * 关键约束：写操作要 admin，support 只能查看；码值在列表里完整显示，因此页面本身要按敏感运营数据对待。
 */
import { useState } from 'react'
import { usersApi, type CodeRow, type GenerateResult } from '@ap/api-client/admin-users'
import { fmtDateTime, useToast } from '@ap/ui'
import { OpsPage, Pager, Stat, Stats, TableWrap, Toolbar } from '../../alice/kit.tsx'
import { EmptyRow, PILL, Pill, Section, ToolInput, ToolSelect } from '../../alice/kit-runtime2.tsx'
import { useU, useQuery, copyText } from '../../alice/kit-users.tsx'
import { BlocksModal, CodeDetailModal, ConfirmDialog, GenerateModal, NoteModal, ResultModal } from '../../alice/g2/CodeModals.tsx'
import { useAdmin } from '../../session.tsx'

const PAGE = 20

export function Codes() {
  const { t, G, err, pet, num, tag, locale } = useU()
  const C = G.codes
  const toast = useToast()
  const { isAdmin } = useAdmin()
  const [draft, setDraft] = useState({ q: '', status: '', batch: '' })
  const [applied, setApplied] = useState(draft)
  const [offset, setOffset] = useState(0)
  const [gen, setGen] = useState(false)
  const [result, setResult] = useState<{ r: GenerateResult | { code: string }; expires: string } | null>(null)
  const [detail, setDetail] = useState<CodeRow | null>(null)
  const [blocks, setBlocks] = useState(false)
  const [noting, setNoting] = useState<{ batch: string; note: string } | null>(null)
  const [confirm, setConfirm] = useState<{ text: string; run: () => Promise<void> } | null>(null)

  const stats = useQuery(() => usersApi.codeStats(), [])
  const list = useQuery(() => usersApi.codes({ q: applied.q, status: applied.status, batch_id: applied.batch, limit: PAGE, offset }), [applied, offset])
  const reloadAll = () => {
    stats.reload()
    list.reload()
  }
  const apply = (next = draft) => {
    setDraft(next)
    setApplied(next)
    setOffset(0)
  }

  const toggle = async (c: CodeRow) => {
    try {
      await usersApi.patchCode(c.id, { status: c.status === 'active' ? 'disabled' : 'active' })
      reloadAll()
    } catch (e) {
      toast.error(err(e))
    }
  }
  const batchToggle = async (batch: string, enable: boolean) => {
    try {
      const r = enable ? await usersApi.enableBatch(batch) : await usersApi.disableBatch(batch)
      const n = 'enabled_count' in r ? r.enabled_count : r.disabled_count
      toast.ok(t(C.batchToggled, { n, act: t(enable ? C.actEnabled : C.actDisabled) }))
      reloadAll()
    } catch (e) {
      toast.error(err(e))
    }
  }
  const exportBatch = () => {
    if (!applied.batch.trim() && !draft.batch.trim()) {
      toast.error(t(C.exportNeedBatch))
      return
    }
    window.location.href = usersApi.exportUrl((draft.batch || applied.batch).trim())
  }

  const s = stats.data
  const rows = list.data?.codes ?? []
  const now = Date.now()
  const dash = t(G.common.dash)

  return (
    <OpsPage eyebrow={t(C.eyebrow)} title={t(C.title)} desc={t(C.desc)} chip={t(C.chip)}>
      <Stats>
        <Stat label={t(C.statTotal)} value={s ? s.total_codes.toLocaleString(tag) : dash} />
        <Stat label={t(C.statValue)} value={s ? pet(s.total_value_units) : dash} sub={t(C.statValueSub)} />
        <Stat label={t(C.statRedeemed)} value={s ? pet(s.redeemed_units) : dash} color="success" />
        <Stat label={t(C.statUsers)} value={s ? s.redeem_users.toLocaleString(tag) : dash} sub={s ? t(C.statCountSub, { n: s.redeemed_count.toLocaleString(tag) }) : undefined} />
      </Stats>

      <Toolbar count={list.data ? t(C.total, { n: list.data.total.toLocaleString(tag) }) : undefined}>
        <ToolInput width={200} placeholder={t(C.searchPh)} value={draft.q} onChange={(e) => setDraft({ ...draft, q: e.target.value })} onEnter={() => apply()} />
        <ToolSelect
          value={draft.status}
          onChange={(e) => apply({ ...draft, status: e.target.value })}
          options={[
            { value: '', label: t(C.statusAll) },
            { value: 'active', label: t(C.statusActive) },
            { value: 'disabled', label: t(C.statusDisabled) },
          ]}
        />
        <ToolInput width={160} placeholder={t(C.batchPh)} value={draft.batch} onChange={(e) => setDraft({ ...draft, batch: e.target.value })} onEnter={() => apply()} />
        <button className="btn btn-secondary btn-sm" onClick={() => apply()}>
          {t(G.common.filter)}
        </button>
        <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto' }} onClick={exportBatch}>
          {t(C.exportBatch)}
        </button>
        <button className="btn btn-secondary btn-sm" onClick={() => setBlocks(true)}>
          {t(C.blocksBtn)}
        </button>
        {isAdmin && (
          <button className="btn btn-primary btn-sm" onClick={() => setGen(true)}>
            {t(C.generate)}
          </button>
        )}
      </Toolbar>

      <TableWrap
        head={
          <>
            <th>{t(C.colCode)}</th>
            <th>{t(C.colAmount)}</th>
            <th>{t(C.colUsed)}</th>
            <th>{t(C.colPerUser)}</th>
            <th>{t(C.colRedeemed)}</th>
            <th>{t(C.colExpires)}</th>
            <th>{t(C.colStatus)}</th>
            <th>{t(C.colBatch)}</th>
            <th>{t(C.colNote)}</th>
            <th>{t(C.colActions)}</th>
          </>
        }
        pager={<Pager offset={offset} limit={PAGE} total={list.data?.total ?? 0} onChange={setOffset} />}
      >
        {list.error != null && <EmptyRow cols={10}>{t(G.common.loadFailed, { msg: err(list.error) })}</EmptyRow>}
        {list.loading && !list.data && <EmptyRow cols={10}>{t(G.common.loading)}</EmptyRow>}
        {list.data && rows.length === 0 && <EmptyRow cols={10}>{t(C.empty)}</EmptyRow>}
        {rows.map((c) => {
          const expired = new Date(c.expires_at).getTime() < now
          return (
            <tr key={c.id}>
              <td>
                <span
                  className="mono"
                  style={{ cursor: 'pointer' }}
                  title={t(G.common.copy)}
                  onClick={async () => toast.ok(t((await copyText(c.code)) ? G.common.copied : G.common.copyFail))}
                >
                  {c.code}
                </span>
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {c.amount_max_units ? `${num(c.amount_units)} ~ ${num(c.amount_max_units)}` : pet(c.amount_units)}
                {c.pool_id !== null ? (
                  <> <Pill tone={PILL.purple}>{t(C.pool)}</Pill></>
                ) : c.amount_max_units ? (
                  <> <Pill tone={PILL.amber}>{t(C.lottery)}</Pill></>
                ) : null}
              </td>
              <td>
                {c.used_count} / {c.max_total_uses}
              </td>
              <td>{c.max_uses_per_user}</td>
              <td>{pet(c.redeemed_units)}</td>
              <td style={{ whiteSpace: 'nowrap' }}>
                {fmtDateTime(c.expires_at, locale)} {expired && <Pill tone={PILL.red}>{t(C.expired)}</Pill>}
              </td>
              <td>
                <Pill tone={c.status === 'active' ? PILL.green : PILL.gray}>{t(c.status === 'active' ? C.statusActive : C.statusDisabled)}</Pill>
              </td>
              <td className="mono" style={{ maxWidth: 130, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.batch_id ?? ''}>
                {c.batch_id || dash}
              </td>
              <td style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.note}>
                {c.note || dash}
              </td>
              <td style={{ whiteSpace: 'nowrap' }}>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => setDetail(c)}>
                    {t(C.detailBtn)}
                  </button>
                  {isAdmin && (
                    <>
                      <button className="btn btn-secondary btn-sm" onClick={() => toggle(c)}>
                        {t(c.status === 'active' ? C.disableBtn : C.enableBtn)}
                      </button>
                      <button
                        className="btn btn-danger btn-sm"
                        onClick={() =>
                          setConfirm({
                            text: t(C.deleteConfirmCode, { code: c.code, n: c.used_count }),
                            run: async () => {
                              await usersApi.deleteCode(c.id)
                              toast.ok(t(C.deleted))
                              setConfirm(null)
                              reloadAll()
                            },
                          })
                        }
                      >
                        {t(C.deleteBtn)}
                      </button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          )
        })}
      </TableWrap>

      <Section title={t(C.batchTitle)} copy={t(C.batchCopy)}>
        <div className="table-inner">
          <table>
            <thead>
              <tr>
                <th>{t(C.batchCols.batch)}</th>
                <th>{t(C.batchCols.note)}</th>
                <th>{t(C.batchCols.codes)}</th>
                <th>{t(C.batchCols.value)}</th>
                <th>{t(C.batchCols.redeemed)}</th>
                <th>{t(C.batchCols.count)}</th>
                <th>{t(C.batchCols.actions)}</th>
              </tr>
            </thead>
            <tbody>
              {s && s.by_batch.length === 0 && <EmptyRow cols={7}>{t(C.empty)}</EmptyRow>}
              {s?.by_batch.map((b) => (
                <tr key={b.batch_id}>
                  <td className="mono">{b.batch_id}</td>
                  <td>{b.note || dash}</td>
                  <td>
                    {b.codes}
                    {b.disabled_codes > 0 && <span style={{ color: 'var(--text-tertiary)' }}> ({t(C.batchDisabled, { n: b.disabled_codes })})</span>}
                  </td>
                  <td>{pet(b.total_value_units)}</td>
                  <td>{pet(b.redeemed_units)}</td>
                  <td>{b.redeemed_count}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => apply({ ...draft, batch: b.batch_id })}
                      >
                        {t(C.batchViewCodes)}
                      </button>
                      <button className="btn btn-secondary btn-sm" onClick={() => (window.location.href = usersApi.exportUrl(b.batch_id))}>
                        {t(C.batchExport)}
                      </button>
                      {isAdmin && (
                        <>
                          {b.disabled_codes < b.codes && (
                            <button className="btn btn-secondary btn-sm" onClick={() => batchToggle(b.batch_id, false)}>
                              {t(C.batchDisableAll)}
                            </button>
                          )}
                          {b.disabled_codes > 0 && (
                            <button className="btn btn-secondary btn-sm" onClick={() => batchToggle(b.batch_id, true)}>
                              {t(C.batchEnableAll)}
                            </button>
                          )}
                          <button className="btn btn-secondary btn-sm" onClick={() => setNoting({ batch: b.batch_id, note: b.note })}>
                            {t(C.batchEditNote)}
                          </button>
                          <button
                            className="btn btn-danger btn-sm"
                            onClick={() =>
                              setConfirm({
                                text: t(C.deleteConfirmBatch, { batch: b.batch_id, n: b.codes }),
                                run: async () => {
                                  await usersApi.deleteBatch(b.batch_id)
                                  toast.ok(t(C.deleted))
                                  setConfirm(null)
                                  reloadAll()
                                },
                              })
                            }
                          >
                            {t(C.batchDelete)}
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      <GenerateModal
        open={gen}
        onClose={() => setGen(false)}
        onDone={(r, expires) => {
          setGen(false)
          setResult({ r, expires })
          reloadAll()
        }}
      />
      <ResultModal result={result?.r ?? null} expiresAt={result?.expires ?? ''} onClose={() => setResult(null)} />
      <CodeDetailModal row={detail} isAdmin={isAdmin} onClose={() => setDetail(null)} onChanged={reloadAll} />
      <BlocksModal open={blocks} isAdmin={isAdmin} onClose={() => setBlocks(false)} />
      <NoteModal
        batch={noting?.batch ?? null}
        initial={noting?.note ?? ''}
        onClose={() => setNoting(null)}
        onDone={(n) => {
          toast.ok(t(C.batchNoteDone, { n }))
          setNoting(null)
          reloadAll()
        }}
      />
      <ConfirmDialog text={confirm?.text ?? null} onClose={() => setConfirm(null)} onConfirm={() => (confirm ? confirm.run() : Promise.resolve())} />
    </OpsPage>
  )
}
